/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import './styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { Mdx, Link } from '@ta-interaktiv/contentbricks'
import { MDXProvider } from '@mdx-js/react'
import { AutofitIFrame } from '@ta-interaktiv/react-autofit-iframe'

import ScrollyStory from 'components/ScrollyStory'

import ContactTracing from 'components/ContactTracing'
import grid1 from 'components/ContactTracing/grid1.json'
import grid2 from 'components/ContactTracing/grid2.json'
import grid3 from 'components/ContactTracing/grid3.json'
import grid3b from 'components/ContactTracing/grid3b.json'
import {
  Cell,
  Mode,
  renderCell
} from 'components/ContactTracing/ContactTracing'
import { COLORS } from 'components/ContactTracing/constants'

if ('scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual'
}

// TODO Set the correct article ID here
const ARTICLE_ID = '11431328'

export function AppComponent() {
  const [globalInfected, setGlobalInfected] = React.useState<string[]>([])

  return (
    <MDXProvider
      components={{
        a: props => <Link allowWordWrap {...props} />,
        h1: ({ children }) => <h1 className='ui image header'>{children}</h1>
      }}
    >
      <div className='index'>
        <Masthead articleId={ARTICLE_ID} />
        <ThemeSwitch />

        <header className='ui vertical relaxed segment cover'>
          <div className='ui container'>
            <Mdx className='title' file={require('text/noIjZ7mQBvNK.mdx')} />
          </div>
        </header>

        <div className='ui vertical segment '>
          <div className='ui text container'>
            <div className='ta author'>
              <nobr>Andreas Moor</nobr>, <nobr>Titus Plattner</nobr>
            </div>
            <div className='ta updated'>Mis à jour le 24 juin</div>

            <br />
            <Mdx file={require('text/b-I8zQ5Ia8Z2.mdx')} />
            <div className='legend'>
              <div
                style={{
                  display: 'flex',
                  marginTop: '1.2rem'
                }}
              >
                {[
                  Mode.healthy,
                  Mode.infected,
                  Mode.infectious,
                  Mode.symptomatic,
                  Mode.quarantined
                ].map((mode, i) => (
                  <div key={mode} style={{ width: '20%' }}>
                    <div style={{ width: 60, margin: 'auto' }}>
                      {i === 0 ? (
                        <Mdx file={require('text/-2w5qzW5xrzQ.mdx')} />
                      ) : (
                        <p>{'\u2000'}</p>
                      )}
                      <svg width={60} height={70}>
                        <g transform='translate(30,40)'>
                          <line
                            x1={-25}
                            y1={-35}
                            x2={25}
                            y2={-35}
                            strokeWidth={1}
                            stroke={i === 0 ? COLORS.player : 'none'}
                          />
                          {renderCell({ mode }, 60)}
                        </g>
                      </svg>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginBottom: '1.2rem' }}>
                <Mdx file={require('text/zmH_i5pCDkJw.mdx')} />
              </div>
            </div>
            <Mdx file={require('text/NkIjCCufM_uV.mdx')} />
            <div style={{ textAlign: 'center', fontSize: '1.3rem' }}>
              <i className='ui icon chevron down' />
            </div>
          </div>
        </div>

        <div className='ui vertical segment'>
          <div className='ui text container chart'>
            <Mdx file={require('text/tQZg95zLfGLg.mdx')} />
          </div>
          <ScrollyStory
            render={(width, height, progress) => (
              <ContactTracing
                width={width}
                height={height}
                progress={progress}
                config={{
                  waypoint: 0.33,
                  waypointModes: [
                    {
                      id: '0-4',
                      mode: Mode.infected
                    }
                  ],
                  title: 'phase 1',
                  playerId: '0-4',
                  grid: grid1 as Cell[]
                }}
              />
            )}
            labels={[
              {
                progress: 0.1,
                content: (
                  <Mdx
                    className='scrollytext'
                    file={require('text/bmuiDosVyS02.mdx')}
                  />
                )
              },
              {
                progress: 0.38,
                content: (
                  <Mdx
                    className='scrollytext'
                    file={require('text/XsBxDrTwEqcF.mdx')}
                  />
                )
              },
              {
                progress: 0.8,
                content: (
                  <Mdx
                    className='scrollytext'
                    file={require('text/njWEzX6yErBt.mdx')}
                  />
                )
              }
            ]}
          />
        </div>

        <div className='ui vertical segment'>
          <div className='ui text container chart'>
            <Mdx file={require('text/f-inOln5goVk.mdx')} />
          </div>
          <ScrollyStory
            render={(width, height, progress) => (
              <ContactTracing
                width={width}
                height={height}
                progress={progress}
                config={{
                  waypoint: 0.32,
                  waypointModes: [
                    {
                      id: '5-6',
                      mode: Mode.infected
                    }
                  ],
                  title: 'phase 2',
                  playerId: '0-4',
                  grid: grid2 as Cell[],
                  setGlobalInfected: setGlobalInfected
                }}
              />
            )}
            labels={[
              {
                progress: 0,
                content: (
                  <Mdx
                    className='scrollytext scrollytextMargin'
                    file={require('text/bspFbfGszz3P.mdx')}
                  />
                )
              },
              {
                progress: 0.4,
                content: (
                  <Mdx
                    className='scrollytext'
                    file={require('text/tJIZBcJ4k4kI.mdx')}
                  />
                )
              },
              {
                progress: 0.7,
                content: (
                  <Mdx
                    className='scrollytext'
                    file={require('text/3lCoFWcLk7c_.mdx')}
                  />
                )
              }
            ]}
          />
        </div>

        <div className='ui vertical segment'>
          <div className='ui text container chart'>
            <Mdx file={require('text/meDLxaW8fl9a.mdx')} />
            <hr />
            <Mdx file={require('text/j0ZKO9_rtdc7.mdx')} />
          </div>
          <ScrollyStory
            length={5}
            render={(width, height, progress) => (
              <ContactTracing
                width={width}
                height={height}
                progress={progress}
                config={{
                  waypoint: 0,
                  waypointModes: [],
                  title: 'phase 3',
                  playerId: '0-4',
                  grid: grid3 as Cell[],
                  moveInfectious: true,
                  infected: globalInfected
                }}
              />
            )}
            labels={[
              {
                progress: 0,
                content: (
                  <Mdx
                    className='scrollytext scrollytextMargin'
                    file={require('text/sEtw9Lkmh5tn.mdx')}
                  />
                )
              },
              {
                progress: 0.1,
                content: (
                  <Mdx
                    className='scrollytext scrollytextMargin'
                    file={require('text/p0YJm02JEg1Y.mdx')}
                  />
                )
              },
              {
                progress: 0.6,
                content: (
                  <Mdx
                    className='scrollytext'
                    file={require('text/XoRj-K00EiBb.mdx')}
                  />
                )
              }
            ]}
          />
        </div>

        <div className='ui vertical segment'>
          <div className='ui text container chart'>
            <hr />
            <Mdx file={require('text/pg92TMwQth1w.mdx')} />
          </div>
          <ScrollyStory
            length={2}
            render={(width, height, progress) => (
              <ContactTracing
                width={width}
                height={height}
                progress={progress}
                config={{
                  title: 'phase 3b',
                  playerId: '0-4',
                  grid: grid3b as Cell[],
                  infected: globalInfected,
                  renderBackground: true,
                  transitions: [
                    {
                      progress: 0.45,
                      from: Mode.infectious,
                      to: Mode.quarantinedInfectious
                    },
                    {
                      progress: 0.45,
                      from: Mode.closeContact,
                      to: Mode.quarantinedHealthy
                    }
                  ]
                }}
              />
            )}
            labels={[
              {
                progress: 0.4,
                content: (
                  <Mdx
                    className='scrollytext'
                    file={require('text/0AynffhkwYcD.mdx')}
                  />
                )
              },
              {
                progress: 0.6,
                content: (
                  <Mdx
                    className='scrollytext'
                    file={require('text/D8sq038pwZIx.mdx')}
                  />
                )
              }
            ]}
          />
        </div>

        <div className='ui vertical segment'>
          <div className='ui text container'>
            <Mdx file={require('text/iMJ2b9KjYe4G.mdx')} />

            <AutofitIFrame
              aria-label='Interactive bar chart'
              src='//datawrapper.dwcdn.net/71LJi/1/'
              style={{ height: '350px' }}
            />
            <Mdx file={require('text/0RfiDknR0eyi.mdx')} />
          </div>
        </div>

        <div className='ui vertical very fitted segment'>
          <div className='ui text container'>
            <div
              style={{
                display: 'flex',
                flexWrap: 'nowrap',
                width: '100%',
                marginBottom: 6
              }}
            >
              <div style={{ width: '49%', marginLeft: '1%' }}>
                <img src={require('images/next-step.png')} />
              </div>
              <div style={{ width: '49%', marginLeft: '1%' }}>
                <img src={require('images/next-step2.png')} />
              </div>
            </div>
            <Mdx
              className='fotolegend'
              file={require('text/v6xOBfyBafur.mdx')}
            />
          </div>
        </div>

        <div className='ui vertical segment'>
          <div className='ui text container'>
            <Mdx file={require('text/ODMri6N70VZo.mdx')} />
          </div>
        </div>

        <div className='ui vertical very fitted segment'>
          <div className='ui text container'>
            <img src={require('images/lab.jpg')} />
            <Mdx
              className='fotolegend'
              file={require('text/0yq4fq8Laluv.mdx')}
            />
          </div>
        </div>

        <div className='ui vertical very fitted segment'>
          <div className='ui text container'>
            <Mdx file={require('text/1afJgqcgEitM.mdx')} />
          </div>
        </div>

        {/* TODO Add your credits in this component */}
        <Footer
          mailTo='atm@tamedia.ch'
          hidePortfolioTeaser
          locale='fr'
          questions={[
            'Ce contenu était-il informatif?',
            'La mise en oeuvre a-t-elle fonctionné?',
            'La mise en forme vous plaît-elle?'
          ]}
          articleId={ARTICLE_ID}
          credits={[['Conception', 'Titus Plattner et Andreas Moor']]}
        />
      </div>
    </MDXProvider>
  )
}
